/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import React from 'react';
import { useConfirmationDialog } from '@opengeoweb/shared';
import type {
  ChangeType,
  DistrictsType,
  MaritimeWarningItem,
  MaritimeWarningsConfig,
  WindDirectionType,
} from './types';
import { isPublishedWarning } from './useMaritimeWarningsFormState';
import { useWarningsTranslation } from '../../utils/i18n';

interface UseConfirmDeleteDialogProps {
  areas: DistrictsType[];
  warnings: MaritimeWarningItem[];
  maritimeConfig: MaritimeWarningsConfig;
  onRemoveWarning: (id: string) => void;
}

interface UseConfirmDeleteDialog {
  onRemoveWarningWithConfirmation: (id: string) => Promise<void>;
}

export function useConfirmDeleteDialog({
  areas,
  warnings,
  maritimeConfig,
  onRemoveWarning,
}: UseConfirmDeleteDialogProps): UseConfirmDeleteDialog {
  const confirmDialog = useConfirmationDialog();
  const { t } = useWarningsTranslation();

  const getLabelFromWindDirectionConfig = React.useCallback(
    (directionConfig: WindDirectionType[], value: string): string => {
      const option = directionConfig.find(
        (direction) => direction.value === value,
      );
      if (!option) {
        return '';
      }
      return t(`maritime-direction-${option.value}`);
    },
    [t],
  );

  const getLabelFromChangeConfig = React.useCallback(
    (changeConfig: ChangeType[], value: string): string => {
      const option = changeConfig.find((change) => change.key === value);
      if (!option) {
        return '';
      }
      return t(`maritime-change-${option.key}`);
    },
    [t],
  );

  const getWarningDescription = React.useCallback(
    (warning: MaritimeWarningItem): string => {
      if (!isPublishedWarning(warning)) {
        return t('maritime-delete-dialog-empty-warning-description');
      }

      const windDirectionFromLabel = getLabelFromWindDirectionConfig(
        maritimeConfig.windDirectionDefinition,
        warning.windDirectionFrom!,
      );

      const areasById = new Map(areas.map((area) => [area.districtName, area]));
      const areaLabels = warning.assignedAreaIds.map(
        (areaId) => areasById.get(areaId)!.districtName,
      );
      const changeToLabel =
        warning.changeType === 'NO_CHANGE'
          ? t('maritime-delete-dialog-no-change')
          : `${getLabelFromChangeConfig(maritimeConfig.changeType, warning.changeType!)} ${getLabelFromWindDirectionConfig(maritimeConfig.windDirectionDefinition, warning.windDirectionTo!)}`;

      const warningDescription = [
        `${windDirectionFromLabel} ${t('maritime-windforce', { value: warning.windForce })}`,
        ...areaLabels,
        changeToLabel,
      ].join(', ');

      return `${warningDescription}. ${t('maritime-delete-dialog-description-suffix')}`;
    },
    [
      getLabelFromWindDirectionConfig,
      maritimeConfig.windDirectionDefinition,
      maritimeConfig.changeType,
      areas,
      t,
      getLabelFromChangeConfig,
    ],
  );

  return {
    onRemoveWarningWithConfirmation: React.useCallback(
      async (id: string) => {
        const warning = warnings.find((warning) => warning.id === id)!;
        await confirmDialog({
          title: t('maritime-delete-warning'),
          description: '',
          confirmLabel: t('maritime-delete-dialog-confirm'),
          cancelLabel: t('close-dialog-cancel'),
          content: getWarningDescription(warning),
        });
        onRemoveWarning(id);
      },
      [warnings, getWarningDescription, t, confirmDialog, onRemoveWarning],
    ),
  };
}
